/**
 * Auth0 Provider Component
 * Wraps the app with Auth0Provider and handles configuration loading
 */

import React, { useEffect, useState } from 'react';
import { Auth0Provider, Auth0ProviderOptions } from '@auth0/auth0-react';
import { getAuth0Config, getAuth0ConfigSync } from '@/lib/auth0Config';
import { Auth0CallbackHandler } from './Auth0CallbackHandler';
import { isNativeMobile } from '@/utils/capacitor';

interface Auth0WrapperProps {
  children: React.ReactNode;
}

function buildAuth0Config(domain: string, clientId: string, audience: string): Auth0ProviderOptions {
  return {
    domain,
    clientId,
    authorizationParams: {
      redirect_uri: isNativeMobile() ? 'com.bytepantry://callback' : window.location.origin,
      audience,
      // Request offline_access so we get a rotating refresh token. Without this,
      // after reopening the app later the access token may be missing/expired
      // and silent auth in a WebView can fail (3rd party cookies) resulting in 401s.
      scope: 'openid profile email offline_access',
    },
    useRefreshTokens: true,
    cacheLocation: 'localstorage',
    // Disabled: falling back to iframe silent auth (prompt=none) when rotation
    // fails causes isLoading to hang for 30–60s. Failing fast sends the user
    // to the landing page immediately, where they can log in again.
    useRefreshTokensFallback: false,
    onRedirectCallback: (appState) => {
      if (isNativeMobile()) {
        // Auth0CallbackHandler handles navigation on native via wouter navigate().
        // A window.location.href reload here would destroy the just-acquired tokens.
        return;
      }
      window.location.href = appState?.returnTo || window.location.pathname;
    },
  };
}

export const Auth0Wrapper: React.FC<Auth0WrapperProps> = ({ children }) => {
  const [auth0Config, setAuth0Config] = useState<Auth0ProviderOptions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [configError, setConfigError] = useState(false);

  useEffect(() => {
    const loadAuth0Config = async () => {
      try {
        const { domain, clientId, audience } = getAuth0ConfigSync() ?? await getAuth0Config();
        setAuth0Config(buildAuth0Config(domain, clientId, audience));
      } catch (error) {
        console.error('Failed to load Auth0 config:', error);
        setConfigError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadAuth0Config();
  }, []);

  if (isLoading) return null;

  if (configError) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '8px' }}>
        <p>Unable to load app configuration. Please check your connection and refresh.</p>
      </div>
    );
  }

  return (
    <Auth0Provider {...auth0Config!}>
      <Auth0CallbackHandler />
      {children}
    </Auth0Provider>
  );
};
